import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  makeStyles, mergeClasses, tokens,
  Dropdown, Option, Button, CounterBadge, Checkbox, RadioGroup, Radio,
  TagGroup, InteractionTag, InteractionTagPrimary, InteractionTagSecondary,
  Accordion, AccordionItem, AccordionHeader, AccordionPanel,
} from '@fluentui/react-components';
import { iconMap } from '../theme/icons';

const FilterIcon = iconMap.filter_list;
const ChevronDownIcon = iconMap.expand_more;
const DismissIcon = iconMap.close;

export const FILTER_CONFIG = {
  // Reikšmės TURI atitikti realų settings.type kodavimą (žr. FurnitureItem.jsx
  // TYPE_LABELS ir FurnitureList.jsx CATEGORY_MAP) — anksčiau id 2-5 buvo sumaišyti
  // su kitokia tvarka, todėl "Tipas" filtras rodydavo klaidingą etiketę ir/ar
  // filtruodavo ne tuos baldus (18 punktų sąrašo #4).
  // `label` čia — ATSARGINĖ (fallback) reikšmė, jei kur nors dar liktų senas
  // tiesioginis `.label` naudojimas; TIKRAS, verčiamas rodomas tekstas visada
  // ateina per `labelKey` + t() (žr. labelOf(opt, t) žemiau).
  types: [
    { id: 0, label: 'Spinta su varstomomis durimis', labelKey: 'furniture_types.type_0' },
    { id: 1, label: 'Spinta su stumdomis durimis', labelKey: 'furniture_types.type_1' },
    { id: 2, label: 'Lentyna', labelKey: 'furniture_types.type_2' },
    { id: 3, label: 'Spintelė', labelKey: 'furniture_types.type_3' },
    { id: 4, label: 'Komoda', labelKey: 'furniture_types.type_4' },
    { id: 5, label: 'TV spintelė', labelKey: 'furniture_types.type_5' },
  ],
  widthRanges: [
    { id: '0-60',   label: '0 – 60 cm',   min: 0,   max: 0.6 },
    { id: '60-120', label: '60 – 120 cm',  min: 0.6, max: 1.2 },
    { id: '120-180',label: '120 – 180 cm', min: 1.2, max: 1.8 },
    { id: '180-240',label: '180 – 240 cm', min: 1.8, max: 2.4 },
    { id: '240-300',label: '240 – 300 cm', min: 2.4, max: 3.0 },
    { id: '>300',   label: '> 300 cm',     min: 3.0, max: 999 },
  ],
  heightRanges: [
    { id: '0-60',   label: '0 – 60 cm',   min: 0,   max: 0.6 },
    { id: '60-120', label: '60 – 120 cm',  min: 0.6, max: 1.2 },
    { id: '120-180',label: '120 – 180 cm', min: 1.2, max: 1.8 },
    { id: '180-220',label: '180 – 220 cm', min: 1.8, max: 2.2 },
    { id: '220-240',label: '220 – 240 cm', min: 2.2, max: 2.4 },
    { id: '>240',   label: '> 240 cm',     min: 2.4, max: 999 },
  ],
  doors: ['0', '1', '2', '3', '4', '5', '6', '> 6'],
  inner: [
    { id: 'drawers', label: 'Su stalčiais', labelKey: 'furniture_filters.inner_drawers' },
    { id: 'racks',   label: 'Su drabužių kabykla', labelKey: 'furniture_filters.inner_racks' },
  ],
  sort: [
    { id: 'price_desc', label: 'Brangiausi viršuje', labelKey: 'furniture_filters.sort_desc' },
    { id: 'price_asc',  label: 'Pigiausi viršuje', labelKey: 'furniture_filters.sort_asc' },
  ],
};

// Griffel makeStyles — literalios reikšmės liko tik ten, kur nėra semantinio
// Fluent token atitikmens (bottom sheet transform/backdrop, kurie yra grynai
// dekoratyvūs/struktūriniai, ne spalva ar tarpas).
const useStyles = makeStyles({
  accordionHeader: { fontWeight: tokens.fontWeightSemibold },
  root: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusXLarge,
    padding: tokens.spacingHorizontalM,
    '@media (min-width: 640px)': { padding: tokens.spacingHorizontalL },
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: tokens.spacingHorizontalM,
    '@media (min-width: 640px)': { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
    '@media (min-width: 1024px)': { gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' },
  },
  // Šoninės juostos (sidebar) variantas — vienas stulpelis, filtrai vienas po kito,
  // naudojamas FurnitureList.jsx kairėje pusėje vietoj horizontalios eilutės viršuje.
  // Kiekvienas filtras čia — VISADA matomas checkbox/radio sąrašas, ne dropdown
  // (vartotojo pageidavimu — nereikia atidaryti/uždaryti, kad pamatytum pasirinkimus).
  gridColumn: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: tokens.spacingVerticalL,
  },
  checkboxList: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, paddingBottom: tokens.spacingVerticalS },
  checkboxRow: { minHeight: 'unset' },
  dropdown: { width: '100%', minWidth: 0 },
  dropdownActive: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    fontWeight: tokens.fontWeightMedium,
  },
  clearOption: {
    color: tokens.colorStatusDangerForeground1,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  chipsRow: {
    marginTop: tokens.spacingVerticalM,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  clearAllButton: { minWidth: 'auto' },
  // mobilus paleidiklis
  mobileTrigger: { width: '100%', justifyContent: 'space-between' },
  mobileTriggerLeft: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  chevron: {
    transitionProperty: 'transform',
    transitionDuration: tokens.durationNormal,
  },
  chevronOpen: { transform: 'rotate(180deg)' },
  // apatinis "sheet" (Fluent Drawer neturi "bottom" pozicijos — struktūra lieka custom)
  backdrop: {
    position: 'fixed', inset: 0, zIndex: tokens.zIndexOverlay,
    backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)',
  },
  sheet: {
    position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: tokens.zIndexPopup,
    backgroundColor: tokens.colorNeutralBackground1,
    borderTopLeftRadius: tokens.borderRadiusXLarge,
    borderTopRightRadius: tokens.borderRadiusXLarge,
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: tokens.shadow28,
    transitionProperty: 'transform',
    transitionDuration: tokens.durationSlower,
    transitionTimingFunction: tokens.curveEasyEase,
  },
  sheetClosed: { transform: 'translateY(100%)' },
  sheetOpen: { transform: 'translateY(0)' },
  sheetHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    paddingLeft: tokens.spacingHorizontalL, paddingRight: tokens.spacingHorizontalL,
    paddingTop: tokens.spacingVerticalM, paddingBottom: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, flexShrink: 0,
  },
  sheetHeaderTitle: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  sheetHeaderActions: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM },
  sheetBody: { flex: 1, overflowY: 'auto', padding: tokens.spacingHorizontalL },
  sheetFooter: {
    paddingLeft: tokens.spacingHorizontalL, paddingRight: tokens.spacingHorizontalL,
    paddingTop: tokens.spacingVerticalM, paddingBottom: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`, flexShrink: 0,
  },
  sheetCta: { width: '100%' },
});

// opt gali būti { id, label, labelKey } arba paprasta eilutė (pvz.
// FILTER_CONFIG.doors) — tas pats dvejopas formatas, kaip originaliame kode.
// `t` neprivalomas — kai jo nėra (arba opt.labelKey nėra), krentame į statinį
// `label`/pačią eilutę.
const idOf = (opt) => (opt !== null && typeof opt === 'object' ? opt.id : opt);
const labelOf = (opt, t) => {
  if (opt === null || typeof opt !== 'object') return opt;
  if (opt.labelKey && t) return t(opt.labelKey);
  return opt.label;
};

const CLEAR_VALUE = '__clear__';

// Visi accordion blokai pradžioje atverti (atkartoja ankstesnę "visada matoma"
// elgseną), bet dabar juos galima suskleisti su rodykle.
const FILTER_ACCORDION_ITEMS = ['type', 'width', 'height', 'doors', 'inner', 'sort'];

// Daugiapasirinkimo filtras (Tipas/Plotis/Aukštis/Durelės/Vidus) — realus Fluent
// `Dropdown multiselect` + `Option` pakeičia buvusį custom mygtuką+panelę su
// checkbox'ais; atidarymo/uždarymo ir "click outside" logiką dabar valdo pats
// Dropdown komponentas.
function FilterMultiSelect({ label, options, value, onChange }) {
  const s = useStyles();
  const { t } = useTranslation();
  const selectedStrings = value.map(String);
  const matched = value.length === 1 ? options.find(o => idOf(o) === value[0]) : undefined;
  const displayLabel = value.length === 0
    ? label
    : value.length === 1
      ? (matched !== undefined ? labelOf(matched, t) : value[0])
      : `${label} (${value.length})`;

  const handleOptionSelect = (_e, data) => {
    if (data.optionValue === CLEAR_VALUE) {
      onChange([]);
      return;
    }
    const nextStrings = new Set(data.selectedOptions.filter(v => v !== CLEAR_VALUE));
    onChange(options.map(idOf).filter(id => nextStrings.has(String(id))));
  };

  return (
    <Dropdown
      multiselect
      placeholder={label}
      value={displayLabel}
      selectedOptions={selectedStrings}
      onOptionSelect={handleOptionSelect}
      className={mergeClasses(s.dropdown, value.length > 0 && s.dropdownActive)}
    >
      {options.map((opt) => {
        const id = idOf(opt);
        const lbl = labelOf(opt, t);
        return (
          <Option key={id} value={String(id)} text={String(lbl)}>
            {lbl}
          </Option>
        );
      })}
      {value.length > 0 && (
        <Option value={CLEAR_VALUE} text={t('furniture_filters.clear')} className={s.clearOption}>
          {t('furniture_filters.clear')}
        </Option>
      )}
    </Dropdown>
  );
}

// Daugiapasirinkimo filtras kaip suskleidžiamas checkbox sąrašas (sidebar/column
// išdėstymui) — standartinis Fluent Accordion (žr. /admin/bugs "Visi filtravimo
// blokai turi būti atskleidžiami/suskleidžiami su rodyklėmis"), kiekviena
// parinktis savo eilutėje po antrašte.
function FilterCheckboxGroup({ label, itemValue, options, value, onChange }) {
  const s = useStyles();
  const { t } = useTranslation();
  const selectedStrings = new Set(value.map(String));

  const toggle = (id) => {
    const idStr = String(id);
    if (selectedStrings.has(idStr)) {
      onChange(value.filter(v => String(v) !== idStr));
    } else {
      onChange([...value, id]);
    }
  };

  return (
    <AccordionItem value={itemValue}>
      <AccordionHeader className={s.accordionHeader}>{label}</AccordionHeader>
      <AccordionPanel>
        <div className={s.checkboxList}>
          {options.map((opt) => {
            const id = idOf(opt);
            const lbl = labelOf(opt, t);
            return (
              <Checkbox
                key={id}
                className={s.checkboxRow}
                checked={selectedStrings.has(String(id))}
                onChange={() => toggle(id)}
                label={String(lbl)}
              />
            );
          })}
        </div>
      </AccordionPanel>
    </AccordionItem>
  );
}

// Vieno pasirinkimo filtras kaip suskleidžiamas radio sąrašas (sidebar/column).
function FilterRadioGroupField({ label, itemValue, options, value, onChange }) {
  const s = useStyles();
  const { t } = useTranslation();
  return (
    <AccordionItem value={itemValue}>
      <AccordionHeader className={s.accordionHeader}>{label}</AccordionHeader>
      <AccordionPanel>
        <RadioGroup value={value || ''} onChange={(_e, data) => onChange(data.value)}>
          <Radio value="" label={t('furniture_filters.default')} />
          {options.map(opt => <Radio key={opt.id} value={opt.id} label={labelOf(opt, t)} />)}
        </RadioGroup>
      </AccordionPanel>
    </AccordionItem>
  );
}

// Vieno pasirinkimo filtras (Rikiavimas) — realus Fluent `Dropdown` (be
// multiselect) + `Option`; "Numatytasis" yra tiesiog Option su value="".
function FilterSortSelect({ label, options, value, onChange }) {
  const s = useStyles();
  const { t } = useTranslation();
  const selected = options.find(o => o.id === value);
  const displayLabel = (selected && labelOf(selected, t)) || label;

  return (
    <Dropdown
      placeholder={label}
      value={displayLabel}
      selectedOptions={value ? [value] : []}
      onOptionSelect={(_e, data) => onChange(data.optionValue)}
      className={mergeClasses(s.dropdown, value && s.dropdownActive)}
    >
      <Option value="">{t('furniture_filters.default')}</Option>
      {options.map(opt => (
        <Option key={opt.id} value={opt.id}>{labelOf(opt, t)}</Option>
      ))}
    </Dropdown>
  );
}

const FurnitureFilters = ({ filters, onFilterChange, layout = 'row' }) => {
  const s = useStyles();
  const { t } = useTranslation();

  const activeCount = [
    filters.type, filters.width, filters.height, filters.doors, filters.inner,
  ].filter(a => a.length > 0).length + (filters.sort ? 1 : 0);

  // #6: bottom sheet mobiliame
  const [sheetOpen, setSheetOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const clearAll = useCallback(() => {
    ['type', 'width', 'height', 'doors', 'inner'].forEach(k => onFilterChange(k, []));
    onFilterChange('sort', '');
  }, [onFilterChange]);

  // Aktyvių filtrų "chip" sąrašas — kiekvienas chip koduoja "kategorija:id" į
  // TagGroup/InteractionTag `value`, kad vienas onDismiss tvarkytų visus.
  const chips = [];
  filters.type.forEach((id) => {
    const opt = FILTER_CONFIG.types.find(ft => ft.id === id);
    if (opt) chips.push({ value: `type:${id}`, label: labelOf(opt, t) });
  });
  filters.width.forEach((id) => {
    const opt = FILTER_CONFIG.widthRanges.find(r => r.id === id);
    if (opt) chips.push({ value: `width:${id}`, label: t('furniture_filters.width_prefix', { value: labelOf(opt, t) }) });
  });
  filters.height.forEach((id) => {
    const opt = FILTER_CONFIG.heightRanges.find(r => r.id === id);
    if (opt) chips.push({ value: `height:${id}`, label: t('furniture_filters.height_prefix', { value: labelOf(opt, t) }) });
  });
  filters.doors.forEach((id) => {
    chips.push({ value: `doors:${id}`, label: t('furniture_filters.doors_prefix', { value: id }) });
  });
  filters.inner.forEach((id) => {
    const opt = FILTER_CONFIG.inner.find(i => i.id === id);
    if (opt) chips.push({ value: `inner:${id}`, label: labelOf(opt, t) });
  });
  if (filters.sort) {
    const opt = FILTER_CONFIG.sort.find(sOpt => sOpt.id === filters.sort);
    if (opt) chips.push({ value: `sort:${filters.sort}`, label: labelOf(opt, t) });
  }

  const handleChipDismiss = useCallback((_e, data) => {
    const sepIdx = data.value.indexOf(':');
    const key = data.value.slice(0, sepIdx);
    const rawId = data.value.slice(sepIdx + 1);
    if (key === 'sort') { onFilterChange('sort', ''); return; }
    const id = key === 'type' ? Number(rawId) : rawId;
    onFilterChange(key, filters[key].filter(v => v !== id));
  }, [filters, onFilterChange]);

  const activeChipsNode = activeCount > 0 ? (
    <TagGroup className={s.chipsRow} onDismiss={handleChipDismiss}>
      {chips.map(chip => (
        <InteractionTag key={chip.value} value={chip.value} shape="rounded" size="small" appearance="brand">
          <InteractionTagPrimary hasSecondaryAction>{chip.label}</InteractionTagPrimary>
          <InteractionTagSecondary aria-label={t('furniture_filters.remove_filter')} />
        </InteractionTag>
      ))}
      <Button appearance="transparent" size="small" className={s.clearAllButton} onClick={clearAll}>
        {t('furniture_filters.clear_all')}
      </Button>
    </TagGroup>
  ) : null;

  const filterGridNode = layout === 'column' ? (
    <Accordion multiple collapsible defaultOpenItems={FILTER_ACCORDION_ITEMS} className={s.gridColumn}>
      <FilterCheckboxGroup itemValue="type" label={t('furniture_filters.type')} options={FILTER_CONFIG.types} value={filters.type} onChange={v => onFilterChange('type', v)} />
      <FilterCheckboxGroup itemValue="width" label={t('furniture_filters.width')} options={FILTER_CONFIG.widthRanges} value={filters.width} onChange={v => onFilterChange('width', v)} />
      <FilterCheckboxGroup itemValue="height" label={t('furniture_filters.height')} options={FILTER_CONFIG.heightRanges} value={filters.height} onChange={v => onFilterChange('height', v)} />
      <FilterCheckboxGroup itemValue="doors" label={t('furniture_filters.doors')} options={FILTER_CONFIG.doors} value={filters.doors} onChange={v => onFilterChange('doors', v)} />
      <FilterCheckboxGroup itemValue="inner" label={t('furniture_filters.inner')} options={FILTER_CONFIG.inner} value={filters.inner} onChange={v => onFilterChange('inner', v)} />
      <FilterRadioGroupField itemValue="sort" label={t('furniture_filters.sort')} options={FILTER_CONFIG.sort} value={filters.sort} onChange={v => onFilterChange('sort', v)} />
    </Accordion>
  ) : (
    <div className={s.grid}>
      <FilterMultiSelect label={t('furniture_filters.type')} options={FILTER_CONFIG.types} value={filters.type} onChange={v => onFilterChange('type', v)} />
      <FilterMultiSelect label={t('furniture_filters.width')} options={FILTER_CONFIG.widthRanges} value={filters.width} onChange={v => onFilterChange('width', v)} />
      <FilterMultiSelect label={t('furniture_filters.height')} options={FILTER_CONFIG.heightRanges} value={filters.height} onChange={v => onFilterChange('height', v)} />
      <FilterMultiSelect label={t('furniture_filters.doors')} options={FILTER_CONFIG.doors} value={filters.doors} onChange={v => onFilterChange('doors', v)} />
      <FilterMultiSelect label={t('furniture_filters.inner')} options={FILTER_CONFIG.inner} value={filters.inner} onChange={v => onFilterChange('inner', v)} />
      <FilterSortSelect label={t('furniture_filters.sort')} options={FILTER_CONFIG.sort} value={filters.sort} onChange={v => onFilterChange('sort', v)} />
    </div>
  );

  // #6: Mobiliame — paleidiklio mygtukas + apatinis "sheet" perdengimas
  if (isMobile) {
    return (
      <div>
        <Button
          appearance="outline"
          className={s.mobileTrigger}
          onClick={() => setSheetOpen(true)}
        >
          <span className={s.mobileTriggerLeft}>
            <FilterIcon fontSize={18} />
            {t('furniture_filters.filters')}
            {activeCount > 0 && <CounterBadge count={activeCount} color="brand" size="small" />}
          </span>
          <ChevronDownIcon fontSize={18} className={mergeClasses(s.chevron, sheetOpen && s.chevronOpen)} />
        </Button>
        {activeChipsNode}

        {/* Apatinio "sheet" backdrop */}
        {sheetOpen && <div className={s.backdrop} onClick={() => setSheetOpen(false)} />}

        {/* Apatinis "sheet" panelis */}
        <div className={mergeClasses(s.sheet, sheetOpen ? s.sheetOpen : s.sheetClosed)}>
          <div className={s.sheetHeader}>
            <span className={s.sheetHeaderTitle}>{t('furniture_filters.filters')}</span>
            <div className={s.sheetHeaderActions}>
              {activeCount > 0 && (
                <Button appearance="transparent" size="small" onClick={clearAll}>{t('furniture_filters.clear')}</Button>
              )}
              <Button appearance="subtle" shape="circular" icon={<DismissIcon />} onClick={() => setSheetOpen(false)} aria-label={t('furniture_filters.close')} />
            </div>
          </div>
          <div className={s.sheetBody}>
            <Accordion multiple collapsible defaultOpenItems={FILTER_ACCORDION_ITEMS} className={s.gridColumn}>
              <FilterCheckboxGroup itemValue="type" label={t('furniture_filters.type')} options={FILTER_CONFIG.types} value={filters.type} onChange={v => onFilterChange('type', v)} />
              <FilterCheckboxGroup itemValue="width" label={t('furniture_filters.width')} options={FILTER_CONFIG.widthRanges} value={filters.width} onChange={v => onFilterChange('width', v)} />
              <FilterCheckboxGroup itemValue="height" label={t('furniture_filters.height')} options={FILTER_CONFIG.heightRanges} value={filters.height} onChange={v => onFilterChange('height', v)} />
              <FilterCheckboxGroup itemValue="doors" label={t('furniture_filters.doors')} options={FILTER_CONFIG.doors} value={filters.doors} onChange={v => onFilterChange('doors', v)} />
              <FilterCheckboxGroup itemValue="inner" label={t('furniture_filters.inner')} options={FILTER_CONFIG.inner} value={filters.inner} onChange={v => onFilterChange('inner', v)} />
              <FilterRadioGroupField itemValue="sort" label={t('furniture_filters.sort')} options={FILTER_CONFIG.sort} value={filters.sort} onChange={v => onFilterChange('sort', v)} />
            </Accordion>
          </div>
          <div className={s.sheetFooter}>
            <Button appearance="primary" size="large" className={s.sheetCta} onClick={() => setSheetOpen(false)}>
              {t('furniture_filters.show_results')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Desktop — originalus inline išdėstymas
  return (
    <div className={s.root}>
      {filterGridNode}
      {activeChipsNode}
    </div>
  );
};

export default FurnitureFilters;
